/* Kkumi Instagram reels — sections/kkumi-instagram-reels.liquid
 *
 * Fetches the @kkumi.au feed from the kkumi-instagram-feed Cloudflare Worker
 * (apps/workers/kkumi-instagram-feed.js) and replaces the server-rendered
 * skeleton tiles with the real thing.
 *
 * Every visible string comes from section settings via data-* attributes on
 * the root (the in-code strings here are last-resort fallbacks only), and
 * every value that came from the API goes in through textContent / attribute
 * setters — never innerHTML.
 *
 * Performance is the whole risk of this section: 9–11 videos in one viewport.
 * So videos are never mounted up front. A tile paints its poster <img>; the
 * <video> is created on the first hover/focus (or, on touch with autoplay
 * enabled, when the tile reaches the middle of the screen), preload="metadata"
 * only, and at most ONE plays at a time.
 */
(function () {
  'use strict';

  var FALLBACK = {
    tileLabel: 'Watch this reel on Instagram',
    mutedLabel: 'muted',
    emptyText: 'No reels to show right now.',
    errorText: "We couldn't load the feed just now.",
    metaTemplate: '[followers] followers · [reels] reels',
    timeToday: 'today',
    timeYesterday: 'yesterday',
    timeDays: '[n] days ago',
    timeWeeks: '[n] weeks ago',
  };

  var finePointer = window.matchMedia('(pointer: fine)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── formatting ────────────────────────────────────────────────── */

  function compactCount(n) {
    if (!Number.isFinite(n)) return '';
    if (n < 1000) return String(n);
    // One decimal until the number gets long: 41.2K reads as a real count,
    // 41K reads as a rounded guess.
    if (n < 1000000) {
      var k = n / 1000;
      return (k < 100 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k)) + 'K';
    }
    var m = n / 1000000;
    return (m < 100 ? m.toFixed(1).replace(/\.0$/, '') : Math.round(m)) + 'M';
  }

  function relativeDate(iso, cfg) {
    if (!iso) return '';
    var then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    var days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days <= 0) return cfg.timeToday;
    if (days === 1) return cfg.timeYesterday;
    if (days < 7) return cfg.timeDays.replace('[n]', String(days));
    if (days < 28) return cfg.timeWeeks.replace('[n]', String(Math.floor(days / 7)));
    return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  /** First line of a caption, trimmed to something a one-line title can hold. */
  function captionTitle(caption) {
    var line = String(caption || '').split('\n')[0].trim();
    return line.length > 48 ? line.slice(0, 47).trim() + '…' : line;
  }

  /* ── config ────────────────────────────────────────────────────── */

  function readConfig(root) {
    var d = root.dataset;
    return {
      feedUrl: (d.feedUrl || '').trim(),
      layout: d.layout || 'band',
      tiles: Math.max(1, parseInt(d.tiles, 10) || 11),
      profileUrl: d.profileUrl || '',
      handle: d.handle || '',
      metaTemplate: d.metaTemplate || FALLBACK.metaTemplate,
      tileLabel: d.tileLabel || FALLBACK.tileLabel,
      mutedLabel: d.mutedLabel || FALLBACK.mutedLabel,
      emptyText: d.emptyText || FALLBACK.emptyText,
      errorText: d.errorText || FALLBACK.errorText,
      timeToday: d.timeToday || FALLBACK.timeToday,
      timeYesterday: d.timeYesterday || FALLBACK.timeYesterday,
      timeDays: d.timeDays || FALLBACK.timeDays,
      timeWeeks: d.timeWeeks || FALLBACK.timeWeeks,
      showViews: d.showViews === '1',
      showMeta: d.showMeta === '1',
      showCaptions: d.showCaptions === '1',
      mobileAutoplay: d.mobileAutoplay === '1',
      avatarOverride: d.avatarOverride || '',
      designMode: d.designMode === '1',
    };
  }

  /** The worker's data endpoint, whether the merchant pasted the base or /feed. */
  function feedEndpoint(base) {
    try {
      var url = new URL(base, window.location.href);
      if (!/\/feed\/?$/.test(url.pathname)) {
        url.pathname = url.pathname.replace(/\/+$/, '') + '/feed';
      }
      return url.toString();
    } catch (e) {
      return '';
    }
  }

  /* ── the section ───────────────────────────────────────────────── */

  function Reels(root) {
    this.root = root;
    this.cfg = readConfig(root);
    this.track = root.querySelector('[data-kkig-track]');
    this.stage = root.querySelector('.kkig__stage');
    this.msg = root.querySelector('[data-kkig-msg]');
    this.dots = root.querySelector('[data-kkig-dots]');
    this.prev = root.querySelector('[data-kkig-prev]');
    this.next = root.querySelector('[data-kkig-next]');
    this.playing = null;
    this.load();
  }

  Reels.prototype.load = function () {
    var self = this;
    var endpoint = this.cfg.feedUrl ? feedEndpoint(this.cfg.feedUrl) : '';

    // No worker configured yet: the header and Follow button are the section.
    if (!endpoint) {
      this.fail(this.cfg.emptyText);
      return;
    }

    fetch(endpoint, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .then(function (data) {
        var reels = (data && data.reels) || [];
        if (!reels.length) {
          self.fail(self.cfg.emptyText);
          return;
        }
        self.renderAccount((data && data.account) || {});
        self.renderTiles(reels.slice(0, self.cfg.tiles));
        self.bindTrack();
      })
      .catch(function () {
        self.fail(self.cfg.errorText);
      });
  };

  /** Empty and error land in the same place: no tiles, header still works. */
  Reels.prototype.fail = function (text) {
    if (this.stage) this.stage.hidden = true;
    var hint = this.root.querySelector('.kkig__hint');
    if (hint) hint.hidden = true;
    if (this.msg && text) {
      this.msg.textContent = text;
      this.msg.hidden = false;
    }
  };

  Reels.prototype.renderAccount = function (account) {
    var cfg = this.cfg;
    var photo = cfg.avatarOverride || account.profile_picture_url || '';
    if (photo) {
      Array.prototype.forEach.call(this.root.querySelectorAll('[data-kkig-avatar]'), function (node) {
        node.style.backgroundImage = 'url("' + encodeURI(photo).replace(/"/g, '%22') + '")';
        node.setAttribute('data-kkig-has-photo', '1');
      });
    }

    var meta = this.root.querySelector('[data-kkig-meta]');
    if (!meta || !cfg.showMeta) return;

    var followers = compactCount(Number(account.followers_count));
    var reels = compactCount(Number(account.media_count));
    // The counts are real or they are absent — never a placeholder number.
    if (!followers && !reels) return;

    meta.textContent = '';
    var parts = cfg.metaTemplate.split(/(\[followers\]|\[reels\])/);
    parts.forEach(function (part) {
      if (part === '[followers]' || part === '[reels]') {
        var value = part === '[followers]' ? followers : reels;
        meta.appendChild(window.KKEl('b', null, value || '—'));
      } else if (part) {
        meta.appendChild(document.createTextNode(part));
      }
    });
    meta.hidden = false;
  };

  Reels.prototype.renderTiles = function (reels) {
    var self = this;
    var cfg = this.cfg;
    if (!this.track) return;

    var hero = cfg.layout === 'band' || cfg.layout === 'grid';
    var frag = document.createDocumentFragment();
    var rest = reels.slice();

    if (hero && rest.length) {
      frag.appendChild(this.tile(rest.shift(), true));
    }

    if (cfg.layout === 'band') {
      // Mosaic: two standard tiles per column beside the hero.
      for (var i = 0; i < rest.length; i += 2) {
        var stack = window.KKEl('span', 'kkig__stack');
        stack.appendChild(self.tile(rest[i], false));
        if (rest[i + 1]) stack.appendChild(self.tile(rest[i + 1], false));
        frag.appendChild(stack);
      }
    } else {
      rest.forEach(function (reel) {
        frag.appendChild(self.tile(reel, false));
      });
    }

    this.track.textContent = '';
    this.track.appendChild(frag);
  };

  Reels.prototype.tile = function (reel, isHero) {
    var cfg = this.cfg;
    var self = this;

    var a = window.KKEl('a', 'kkig__tile' + (isHero ? ' kkig__tile--hero' : ''));
    a.href = reel.permalink || cfg.profileUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('data-kkig-tile', reel.id || '');
    var caption = captionTitle(reel.caption);
    a.setAttribute('aria-label', caption ? cfg.tileLabel + ': ' + caption : cfg.tileLabel);

    var media = window.KKEl('span', 'kkig__media');
    if (reel.poster_url) {
      var img = window.KKEl('img');
      img.src = reel.poster_url;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('load', function () {
        a.classList.add('has-media');
      });
      media.appendChild(img);
    }
    a.appendChild(media);
    a._kkMedia = media;
    a._kkVideoSrc = reel.video_url || '';

    // Unfilled / still-loading slot marker.
    var wm = window.KKEl('span', 'kkig__wm');
    wm.innerHTML = window.KKIcons.html('instagram');
    wm.setAttribute('aria-hidden', 'true');
    a.appendChild(wm);

    var play = window.KKEl('span', 'kkig__play');
    play.innerHTML = window.KKIcons.html('play');
    play.setAttribute('aria-hidden', 'true');
    a.appendChild(play);

    var showsCaption = isHero && cfg.showCaptions && cfg.layout === 'grid' && !!caption;

    // The hero caption owns the bottom of the tile; a view count there would
    // sit on top of it.
    if (!showsCaption && cfg.showViews && Number.isFinite(Number(reel.view_count)) && Number(reel.view_count) > 0) {
      var views = window.KKEl('span', 'kkig__views');
      views.innerHTML = window.KKIcons.html('play');
      views.appendChild(document.createTextNode(compactCount(Number(reel.view_count))));
      a.appendChild(views);
    }

    a.appendChild(window.KKEl('span', 'kkig__mute', cfg.mutedLabel));

    var bar = window.KKEl('span', 'kkig__bar');
    bar.setAttribute('aria-hidden', 'true');
    bar.appendChild(window.KKEl('i'));
    a.appendChild(bar);

    if (showsCaption) {
      var cap = window.KKEl('span', 'kkig__cap');
      cap.appendChild(window.KKEl('b', null, caption));
      var when = relativeDate(reel.timestamp, cfg);
      if (when) cap.appendChild(window.KKEl('span', null, when));
      a.appendChild(cap);
    }

    if (reel.video_url && !reducedMotion.matches) {
      if (finePointer.matches) {
        a.addEventListener('mouseenter', function () { self.play(a); });
        a.addEventListener('mouseleave', function () { self.stop(a); });
      }
      // Keyboard users get the same treatment as hover.
      a.addEventListener('focus', function () { self.play(a); });
      a.addEventListener('blur', function () { self.stop(a); });
      if (!finePointer.matches && cfg.mobileAutoplay) this.observe(a);
    }

    return a;
  };

  /* ── playback ──────────────────────────────────────────────────── */

  Reels.prototype.mount = function (tile) {
    if (tile._kkVideo || !tile._kkVideoSrc) return tile._kkVideo;
    var video = document.createElement('video');
    video.muted = true; // `muted` + `playsinline` are both required for autoplay
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.preload = 'metadata'; // never "auto" — 9-11 of these share a viewport
    video.src = tile._kkVideoSrc;
    var bar = tile.querySelector('.kkig__bar i');
    if (bar) {
      video.addEventListener('timeupdate', function () {
        if (!video.duration) return;
        bar.style.width = (video.currentTime / video.duration) * 100 + '%';
      });
    }
    tile._kkMedia.appendChild(video);
    tile._kkVideo = video;
    return video;
  };

  Reels.prototype.play = function (tile) {
    if (reducedMotion.matches) return;
    if (this.playing && this.playing !== tile) this.stop(this.playing);
    var video = this.mount(tile);
    if (!video) return;
    this.playing = tile;
    tile.classList.add('is-playing', 'has-media');
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(function () { /* autoplay refused — poster stands */ });
  };

  Reels.prototype.stop = function (tile) {
    var video = tile._kkVideo;
    tile.classList.remove('is-playing');
    if (this.playing === tile) this.playing = null;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    var bar = tile.querySelector('.kkig__bar i');
    if (bar) bar.style.width = '0%';
  };

  /** Touch autoplay: only ever the tile nearest the middle of the screen. */
  Reels.prototype.observe = function (tile) {
    var self = this;
    if (!('IntersectionObserver' in window)) return;
    if (!this.io) {
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) self.play(entry.target);
            else if (self.playing === entry.target) self.stop(entry.target);
          });
        },
        { threshold: 0.85, rootMargin: '-25% 0px -25% 0px' }
      );
    }
    this.io.observe(tile);
  };

  /* ── the scroll track ──────────────────────────────────────────── */

  Reels.prototype.bindTrack = function () {
    var self = this;
    var track = this.track;
    if (!track || this.cfg.layout === 'grid') return;

    var scrollable = function () {
      return track.scrollWidth - track.clientWidth > 4;
    };

    if (this.prev && this.next) {
      this.prev.hidden = !scrollable();
      this.next.hidden = !scrollable();
      this.prev.addEventListener('click', function () {
        track.scrollBy({ left: -Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
      });
      this.next.addEventListener('click', function () {
        track.scrollBy({ left: Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
      });
    }

    var pages = 1;
    if (this.dots) {
      pages = Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth)));
      this.dots.textContent = '';
      for (var i = 0; i < pages; i++) this.dots.appendChild(window.KKEl('i'));
      this.dots.hidden = pages < 2;
    }

    var sync = function () {
      var max = track.scrollWidth - track.clientWidth;
      if (self.stage) self.stage.classList.toggle('is-end', track.scrollLeft >= max - 4);
      if (self.prev) self.prev.disabled = track.scrollLeft <= 4;
      if (self.next) self.next.disabled = track.scrollLeft >= max - 4;
      if (!self.dots) return;
      var page = max <= 0 ? 0 : Math.round((track.scrollLeft / max) * (pages - 1));
      Array.prototype.forEach.call(self.dots.children, function (dot, idx) {
        dot.classList.toggle('is-on', idx === page);
      });
    };

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', sync);
    sync();
  };

  /* ── boot ──────────────────────────────────────────────────────── */

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-kkig]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.kkBound) return;
      root.dataset.kkBound = '1';
      new Reels(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor: a re-rendered section arrives with fresh, unbound markup.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
